import { useCallback, useMemo } from "react";

import { KeyboardBackspace } from "@material-ui/icons";
import { FRAC_COLOR, FRAC_LAYER, RIG_COLOR } from "constants/mapLayers.constants";
import { RIG_FRAC_FIELD } from "constants/rigFrac.constants";
import {
  selectHeldFocusColors,
  useFocusColorHoldStore
} from "stores/focusColorHoldStore";
import {
  RigFracColorSource,
  selectHiddenRigFracValues,
  selectLayerFilterSettings,
  selectRigFracColorByDetached,
  selectRigFracColorSource,
  selectRigFracFocusColorBy,
  selectSelectedRigFracLayer,
  useMapStore
} from "stores/map";
import styled from "styled-components";
import { normalizeMatchKey, resolveFocusPaint } from "utils/color/focusColorSync";
import { formatRigFracStateLabel } from "utils/map/rigFracValueMapping";

import { useFocusColorSync } from "hooks/focusColorSync";
import { useRigFracBasePalette } from "hooks/rigfrac/useRigFracBasePalette";
import { useRigFracCountyLabel } from "hooks/rigfrac/useRigFracCountyLabel";

import { BaseDropdown, BaseIconToggle, ErrorBoundary } from "components/base";
import { IconSpinner } from "components/icons";
import { buildRigFracColorAssignment } from "components/map/utils/rigFracColorAssignment";
import { useProjectContext } from "components/project/projects/context";
import { useSelectedProjectPermissions } from "components/project/projects/hooks";
import { IColorPalette } from "components/user-settings/models";

import { useUpdateRigFracColorBy } from "../../mutations";
import { useProjectLayerListQuery, useRigFracValuesQuery } from "../../queries";
import { RigFracColorActions } from "./RigFracColorActions";
import { RigFracValueList } from "./RigFracValueList";
import { EmptyStateMessage } from "./emptyStateMessage";
import {
  RigFracColorByOption,
  getRigFracColorByOptions,
  resolveColorByField,
  resolveEffectiveColorByField
} from "./rigFracColorByFields";

// Stable empty references so the "no overrides yet" / "nothing hidden" cases
// don't hand a fresh object/array to the colour and hidden-value memos each
// render (which would recompute them needlessly).
const EMPTY_OVERRIDES: Record<string, string> = {};
const EMPTY_HIDDEN: string[] = [];

export function RigFracDetails(): JSX.Element | null {
  const selection = useMapStore(selectSelectedRigFracLayer);
  const setSelectedRigFracLayer = useMapStore((s) => s.setSelectedRigFracLayer);
  const hiddenByLayer = useMapStore(selectHiddenRigFracValues);
  const setHiddenRigFracValues = useMapStore((s) => s.setHiddenRigFracValues);
  const colorSourceByLayer = useMapStore(selectRigFracColorSource);
  const setRigFracColorSource = useMapStore((s) => s.setRigFracColorSource);
  const focusColorBy = useMapStore(selectRigFracFocusColorBy);
  const colorByDetached = useMapStore(selectRigFracColorByDetached);

  const { selectedProjectId } = useProjectContext();
  const permissions = useSelectedProjectPermissions();
  const canEdit = permissions.canEditProjectItems;

  const layerList = useProjectLayerListQuery({ projectId: selectedProjectId });
  const values = useRigFracValuesQuery();
  const updateColorBy = useUpdateRigFracColorBy();
  const focusResolution = useFocusColorSync();
  // What each attribute last resolved to, shared with the map adapter and the
  // charts so a panel opened mid-load shows the colours the map is showing.
  const heldFocusColors = useFocusColorHoldStore(selectHeldFocusColors);

  const layer = useMemo(
    () => layerList.data?.find((l) => l.layerId === selection?.layerId),
    [layerList.data, selection?.layerId]
  );

  const staticColor = selection?.name === FRAC_LAYER ? FRAC_COLOR : RIG_COLOR;

  // What this layer is SHOWING — the focus's attribute unless the user has
  // picked one by hand. Drives the swatch list, the domain and the map.
  const isDetached = !!selection && !!colorByDetached[selection.layerId];
  const countyLabel = useRigFracCountyLabel();
  const basePalette = useRigFracBasePalette();
  const colorByField = resolveEffectiveColorByField({
    saved: layer?.colorByField,
    focusColorBy,
    isDetached,
    layerName: selection?.name
  });
  // What this layer is SAVED as. Every colour edit below persists THIS, never
  // the displayed field: colorByField is project state shared with the whole
  // team, so recolouring a swatch while the focus is driving the panel must not
  // quietly rewrite which attribute a teammate's layer is coloured by. Overrides
  // are keyed by value, not by attribute, so one made against the focus's domain
  // survives here exactly as it already does across a manual attribute switch.
  const savedColorByField = resolveColorByField(layer?.colorByField, selection?.name);
  const overrides = layer?.colorOverrides ?? EMPTY_OVERRIDES;
  const colorSource = selection ? colorSourceByLayer[selection.layerId] : undefined;

  const domain = values.data?.values?.[colorByField];

  // Identical call to the map adapter's, against the same shared held
  // colours, so the panel and the map cannot reach different answers for the
  // same layer. A resolved matching focus outranks the layer's own palette,
  // solid colour, and per-value overrides; while it is only pending there is
  // no focus colour yet, so an override the user sets during that window
  // still shows up in the list until the real focus colours land.
  const focus = useMemo(
    () =>
      resolveFocusPaint({
        resolution: focusResolution,
        layerAttribute: colorByField,
        held: heldFocusColors[colorByField]
      }),
    [focusResolution, colorByField, heldFocusColors]
  );

  // The whole assignment is built in one memo, on the resolution object itself
  // rather than a per-render literal of its fields, so a resolution that hasn't
  // changed can't invalidate this (and with it RigFracValueList's own memo).
  const { valueColors } = useMemo(
    () =>
      buildRigFracColorAssignment({
        colorByField,
        domain,
        colorSource,
        overrides,
        staticColor,
        focus,
        defaultPalette: basePalette
      }),
    [colorByField, domain, colorSource, overrides, staticColor, focus, basePalette]
  );

  // A value is focus-controlled when the resolved focus paint has a colour
  // for it — its own colour picker is disabled in that case (the focus wins).
  const focusControlledValues = useMemo(() => {
    if (!focus || !domain) {
      return undefined;
    }
    const controlled = new Set<string>();
    for (const value of domain) {
      if (focus.colors.has(normalizeMatchKey(value))) {
        controlled.add(value);
      }
    }
    return controlled;
  }, [focus, domain]);

  const hiddenList = selection
    ? hiddenByLayer[selection.layerId] ?? EMPTY_HIDDEN
    : EMPTY_HIDDEN;
  const hiddenValues = useMemo(() => new Set(hiddenList), [hiddenList]);

  // Values the layer-local filter excludes (EVA-63), shown unchecked in the
  // value list. Derived from the color-by field's OWN selection only — a value
  // is filtered out iff that field has a non-empty selection that omits it.
  // Deliberately NOT from the map-derived facets: those are viewport/tile-scoped,
  // so using them would mark off-screen values unchecked even with no filter set,
  // and flip checkbox state as the user merely pans. The store keys selections by
  // layer NAME (not the layerId used for hidden values), so read by selection.name.
  const layerFilterSettings = useMapStore(selectLayerFilterSettings);
  const filteredOutValues = useMemo(() => {
    const out = new Set<string>();
    if (!selection || !domain) {
      return out;
    }
    const ownSelection = layerFilterSettings[selection.name]?.[colorByField];
    if (!ownSelection || ownSelection.length === 0) {
      return out;
    }
    const ownSet = new Set(ownSelection);
    for (const value of domain) {
      if (!ownSet.has(value)) {
        out.add(value);
      }
    }
    return out;
  }, [selection, domain, colorByField, layerFilterSettings]);

  // Session-only visibility: toggling a value never persists, so no mutation
  // runs here — the map adapter reads the hidden set straight from the store.
  // Stable identity keeps the value list's untouched rows from re-rendering.
  const handleToggleValue = useCallback(
    (value: string, checked: boolean) => {
      if (!selection) {
        return;
      }
      const current = hiddenByLayer[selection.layerId] ?? EMPTY_HIDDEN;
      const next = checked
        ? current.filter((v) => v !== value)
        : current.includes(value)
        ? current
        : [...current, value];
      setHiddenRigFracValues(selection.layerId, next as string[]);
    },
    [selection, hiddenByLayer, setHiddenRigFracValues]
  );

  const handleOverride = useCallback(
    (value: string, color: string) => {
      if (!canEdit || !selectedProjectId || !selection) {
        return;
      }
      updateColorBy.mutate({
        projectId: selectedProjectId,
        layerId: selection.layerId,
        // The layer's own saved field, never the displayed one; for a never-set
        // layer this writes the Current Operator default, as before.
        colorByField: savedColorByField,
        colorOverrides: { ...overrides, [value]: color }
      });
    },
    [
      canEdit,
      selectedProjectId,
      selection,
      savedColorByField,
      overrides,
      updateColorBy.mutate
    ]
  );

  if (!selection) {
    return null;
  }

  const colorByOptions = [...getRigFracColorByOptions(selection.name)];
  const selectedOption = colorByOptions.find((option) => option.value === colorByField);

  const handleColorByChange = (option: RigFracColorByOption) => {
    if (!canEdit || !selectedProjectId) {
      return;
    }
    // A pick is a no-op only when it would leave the layer exactly as it
    // already is: saved as this attribute AND not following the focus. Picking
    // the attribute the focus is currently pushing is NOT a no-op — it means
    // "keep this layer here when the focus moves on", which is the only way to
    // pin a layer to what it is showing.
    const isAlreadyPinnedHere =
      option.value === savedColorByField && (isDetached || focusColorBy == null);
    if (isAlreadyPinnedHere) {
      return;
    }
    // Overrides are retained across attribute switches (they stay dormant until
    // the value reappears in the active domain). detachFromFocus stops this
    // layer following the focus, applied inside the mutation's own optimistic
    // transaction so it rolls back with the cache if the save fails.
    updateColorBy.mutate({
      projectId: selectedProjectId,
      layerId: selection.layerId,
      colorByField: option.value,
      colorOverrides: overrides,
      detachFromFocus: true
    });
    // Hidden values track the previous attribute's domain, so switching
    // attributes resets the set for this layer.
    setHiddenRigFracValues(selection.layerId, []);
  };

  // Shared tail for the two full-domain colour actions: the same edit-permission
  // + domain guard, the persist, and remembering the colour source so a later
  // Color By switch re-applies the user's choice to the new domain.
  //
  // These two persist the DISPLAYED field and pin the layer to it, unlike the
  // per-swatch override above, which persists the saved field. The difference
  // is that these replace `colorOverrides` wholesale with a map built over the
  // displayed domain: writing that against a different saved attribute would
  // delete every override the team had under it and leave the layer with a set
  // of colours keyed to values its own attribute never produces. Recolouring
  // the entire visible domain is an explicit statement about the attribute on
  // screen, so the attribute is saved with the colours rather than silently
  // diverging from them.
  const applyDomainColors = (
    colorOverrides: Record<string, string>,
    source: RigFracColorSource
  ) => {
    if (!canEdit || !selectedProjectId || !domain || domain.length === 0) {
      return;
    }
    updateColorBy.mutate({
      projectId: selectedProjectId,
      layerId: selection.layerId,
      colorByField,
      colorOverrides,
      detachFromFocus: true
    });
    setRigFracColorSource(selection.layerId, source);
  };

  // Full-replace: colour every value in the current domain with the chosen
  // palette and persist them as explicit overrides (unlike per-value hiding).
  // Remember the palette so a later Color By switch re-applies it to the new
  // domain instead of falling back to the Plasma default.
  const handleApplyPalette = (chosenPalette: IColorPalette) => {
    const source: RigFracColorSource = { kind: "palette", palette: chosenPalette };
    const { valueColors: paletteColors } = buildRigFracColorAssignment({
      colorByField,
      domain,
      colorSource: source,
      overrides: EMPTY_OVERRIDES,
      staticColor
    });
    applyDomainColors(Object.fromEntries(paletteColors), source);
  };

  // Full-replace with a single colour: paint every value in the current domain
  // with the chosen hex. Remember the single colour so a later Color By switch
  // paints the new domain with it instead of the Plasma default.
  const handleColorAll = (color: string) => {
    applyDomainColors(Object.fromEntries((domain ?? []).map((value) => [value, color])), {
      kind: "solid",
      color
    });
  };

  const hasDomain = !!domain && domain.length > 0;
  const isDomainDegraded = !values.isFetching && (!!values.error || !hasDomain);

  return (
    <DetailsContainer id="rig_frac_details_container">
      <HeaderContainer>
        <BaseIconToggle size={0}>
          <KeyboardBackspace
            fontSize="large"
            onClick={() => setSelectedRigFracLayer(null)}
            data-testid="rig-frac-details-back"
          />
        </BaseIconToggle>
        <Title title={`${selection.name} Details`}>{selection.name} Details</Title>
      </HeaderContainer>

      <ColorByContainer>
        <ColorByLabel>Colour By</ColorByLabel>
        <ColorByRow>
          <DropdownWrapper $readOnly={!canEdit}>
            <BaseDropdown
              className="rig-frac-color-by-dropdown"
              options={colorByOptions}
              labelKey="label"
              value={selectedOption}
              onChange={canEdit ? handleColorByChange : undefined}
              testid="rig-frac-color-by-dropdown"
            />
          </DropdownWrapper>
          {canEdit && (
            <RigFracColorActions
              isDisabled={!hasDomain}
              onApplyPalette={handleApplyPalette}
              onColorAll={handleColorAll}
            />
          )}
        </ColorByRow>
      </ColorByContainer>

      <ErrorBoundary>
        <BodyContainer>
          {values.isFetching && (
            <LoadingContainer data-testid="rig-frac-values-loading">
              <IconSpinner />
            </LoadingContainer>
          )}
          {isDomainDegraded && (
            <EmptyStateMessage data-testid="rig-frac-values-degraded">
              No values are available for this attribute right now. The layer is shown in
              its default colour.
            </EmptyStateMessage>
          )}
          {!isDomainDegraded && hasDomain && (
            <RigFracValueList
              values={domain as string[]}
              valueColors={valueColors}
              isReadOnly={!canEdit}
              hiddenValues={hiddenValues}
              filteredOutValues={filteredOutValues}
              focusControlledValues={focusControlledValues}
              onOverride={handleOverride}
              onToggleValue={handleToggleValue}
              formatLabel={
                colorByField === RIG_FRAC_FIELD.County
                  ? countyLabel
                  : colorByField === RIG_FRAC_FIELD.StateProvince
                  ? formatRigFracStateLabel
                  : undefined
              }
            />
          )}
        </BodyContainer>
      </ErrorBoundary>
    </DetailsContainer>
  );
}

const DetailsContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  min-width: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const HeaderContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 4px;

  margin-top: 10px;
  padding-left: 14px;
  padding-right: 23px;

  font-weight: var(--fontWeightBold);

  svg {
    color: #a2aaad;
    transition: color var(--duration);
    cursor: pointer;
    &:hover {
      color: var(--color-text);
    }
  }
`;

const Title = styled.div`
  padding-left: 6px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const ColorByContainer = styled.div`
  padding: 10px 14px 0px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: var(--fontWeightBold);

  .rig-frac-color-by-dropdown {
    height: 30px;
    width: 100%;
  }
`;

// Dropdown on the left, colour-actions icon beside it, matching the shapefile
// property selector row. Gap is a touch wider than the shapefile row's 4px so
// the icon doesn't feel cramped against the dropdown edge.
const ColorByRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 2px;

  .ant-btn {
    color: #a2aaad;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    height: 30px;
  }
  .ant-btn:hover {
    color: var(--color-primary);
  }
`;

const ColorByLabel = styled.label`
  font-size: 13px;
`;

const DropdownWrapper = styled.div<{ $readOnly: boolean }>`
  flex: 1;
  min-width: 0;
  pointer-events: ${({ $readOnly }) => ($readOnly ? "none" : "auto")};
  opacity: ${({ $readOnly }) => ($readOnly ? 0.6 : 1)};
`;

const BodyContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
`;
